import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { GeodataModel as GeodataModelV0 } from '../../../../../../typings/api/skymap/rest/v0/.common';
import { GeodataModel } from '../../../../../../typings/api/skymap/rest/v1/.common';
import { formattedDateTime } from '../../../../utils/dateUtils';
import { toHumanReadableFileSize } from '../../../../utils/io/file';
import { gsdFromMeters } from '../../../../utils/proj4/units';
import { isDefined } from '../../../../utils/variables';
import { useGsdUnit } from '../../../hooks/use-gsd-unit';
import { useOptionalUserState } from '../../../state/user-state/user-state';
import { OverlayLoader } from '../../overlay-loader/overlay-loader';
import { translatedProcessType } from '../geodata';

interface Props {
  selectedItem: GeodataModel;
  loadedItem?: GeodataModelV0;
  boundaryPolygonCount?: number;
  isBoundaryPolygonsPending?: boolean;
}

const GeodataDetails = (props: Props) => {
  const { t } = useTranslation('cloudProcessing');
  const userState = useOptionalUserState();
  const isOrganizationAdmin = userState?.isOrganizationAdmin ?? false;
  const gsdUnit = useGsdUnit();
  const uploadedByName = useMemo(() => {
    if (!isDefined(props.loadedItem?.uploadedBy)) {
      return undefined;
    }

    return `${props.loadedItem.uploadedBy.firstName} ${props.loadedItem.uploadedBy.lastName}`;
  }, [props.loadedItem]);
  const filteredPointCloudInfo = useMemo(() => {
    if (!isDefined(props.loadedItem?.filteredPointCloud)) {
      return undefined;
    }

    return `${props.loadedItem.filteredPointCloud.format} (${props.loadedItem.filteredPointCloud.spacing}, ${
      props.loadedItem.filteredPointCloud.class === 'GROUND'
        ? t('setupDialog.addOns.filteredPointCloud.classesGround', { ns: 'cloudProcessing' })
        : t('setupDialog.addOns.filteredPointCloud.classesAll', { ns: 'cloudProcessing' })
    })`;
  }, [props.loadedItem, t]);

  const isOrthoProcess = ['ORTHO_ONLY', 'ORTHO_TIF'].includes(props.selectedItem.processType);
  const size = props.selectedItem.size ?? 0;
  const sizeGlacier = props.selectedItem.sizeGlacier ?? 0;

  return (
    <OverlayLoader visible={!isDefined(props.loadedItem)}>
      <Component>
        <h5>{t('geodataPage.details.title', { ns: 'cloudProcessing' })}</h5>
        <InfoRow>
          <InfoItem>
            <label id="process-type">
              {t('geodataPage.details.package', { ns: 'cloudProcessing' })}
            </label>
            <span aria-labelledby="process-type">
              {translatedProcessType(props.selectedItem.processType, t)}
            </span>
          </InfoItem>

          {(props.selectedItem.processType !== 'ORTHO_TIF' ||
            !props.selectedItem.isLegacyProcess) && (
            <>
              <InfoItem>
                <label id="photo-count">
                  {t('geodataPage.details.photoCount', { ns: 'cloudProcessing' })}
                </label>
                <span aria-labelledby="photo-count">{props.selectedItem.photoCount}</span>
              </InfoItem>

              {!props.selectedItem.isLegacyProcess && (
                <InfoItem>
                  <label id="photo-count">
                    {t('geodataPage.details.gcpCount', { ns: 'cloudProcessing' })}
                  </label>
                  <span aria-labelledby="photo-count">
                    {props.loadedItem?.gcpCollection?.points.length ?? 0}
                  </span>
                </InfoItem>
              )}
            </>
          )}
        </InfoRow>

        {size + sizeGlacier > 0 && (
          <InfoRow>
            <InfoItem>
              <label id="size">
                {t('geodataPage.details.totalSize', { ns: 'cloudProcessing' })}
              </label>
              <span aria-labelledby="size">{toHumanReadableFileSize(size + sizeGlacier)}</span>
            </InfoItem>
            {isOrganizationAdmin && (
              <InfoItem>
                <label id="size-glacier">
                  {t('geodataPage.details.glacierSize', { ns: 'cloudProcessing' })}
                </label>
                <span aria-labelledby="size-glacier">{toHumanReadableFileSize(sizeGlacier)}</span>
              </InfoItem>
            )}
          </InfoRow>
        )}

        <InfoRow>
          <InfoItem>
            <label id="uploaded">
              {t('geodataPage.details.uploaded', { ns: 'cloudProcessing' })}
            </label>
            <span aria-labelledby="uploaded">{formattedDateTime(props.selectedItem.uploaded)}</span>
          </InfoItem>

          {uploadedByName && (
            <InfoItem>
              <label id="uploaded-by">
                {t('geodataPage.details.uploadedBy', { ns: 'cloudProcessing' })}
              </label>
              <span aria-labelledby="uploaded-by">{uploadedByName}</span>
            </InfoItem>
          )}
        </InfoRow>
        <InfoRow>
          {props.selectedItem.isLegacyProcess ? (
            <InfoItem>
              <label id="boundary-file">
                {t('geodataPage.details.boundary', { ns: 'cloudProcessing' })}
              </label>
              <span aria-labelledby="boundary-file">
                {props.selectedItem.boundaryFile ?? t('no', { ns: 'common' })}
              </span>
            </InfoItem>
          ) : (
            !props.isBoundaryPolygonsPending && (
              <InfoItem>
                <label id="boundary-polygon-count">
                  {t('geodataPage.details.boundaryPolygonCount', { ns: 'cloudProcessing' })}
                </label>
                <span aria-labelledby="boundary-polygon-count">
                  {props.boundaryPolygonCount ?? 0}
                </span>
              </InfoItem>
            )
          )}

          {props.selectedItem.processType !== 'ORTHO_TIF' && (
            <InfoItem>
              <label id="gsd">
                {t('geodataPage.details.gsd', { ns: 'cloudProcessing', unit: `${gsdUnit}/px` })}
              </label>
              <span aria-labelledby="gsd">{gsdFromMeters(props.selectedItem.gsd, gsdUnit)}</span>
            </InfoItem>
          )}

          <InfoItem>
            <label id="rolling-shutter">
              {t('geodataPage.details.rollingShutter', { ns: 'cloudProcessing' })}
            </label>
            <span aria-labelledby="rolling-shutter">
              {props.selectedItem.rollingShutter
                ? t('yes', { ns: 'common' })
                : t('no', { ns: 'common' })}
            </span>
          </InfoItem>
        </InfoRow>

        <h5>{t('geodataPage.details.projectionTitle', { ns: 'cloudProcessing' })}</h5>

        {props.loadedItem && (
          <InfoRow>
            <InfoItem>
              <label id="coordinate-system">
                {t('geodataPage.details.coordinateSystem', { ns: 'cloudProcessing' })}
              </label>
              <span aria-labelledby="coordinate-system">
                {props.loadedItem.coordinateSystem?.name}
              </span>
            </InfoItem>

            <InfoItem>
              <label id="geoid">{t('geodataPage.details.geoid', { ns: 'cloudProcessing' })}</label>
              <span aria-labelledby="geoid">
                {props.loadedItem.geoid?.name ??
                  t('geodataPage.details.noGeoid', { ns: 'cloudProcessing' })}
              </span>
            </InfoItem>
          </InfoRow>
        )}

        {!isOrthoProcess && (
          <>
            <h5>{t('geodataPage.details.addOnsTitle', { ns: 'cloudProcessing' })}</h5>
            <InfoRow>
              <InfoItem>
                <label id="gcp-correction">
                  {t('geodataPage.details.gcpAdjustmentOrdered', { ns: 'cloudProcessing' })}
                </label>
                <span aria-labelledby="gcp-correction">
                  {props.loadedItem?.gcpAdjustmentRequest
                    ? t('yes', { ns: 'common' })
                    : t('no', { ns: 'common' })}
                </span>
              </InfoItem>

              {(!isOrthoProcess || !props.selectedItem.isLegacyProcess) && (
                <>
                  <InfoItem>
                    <label id="terrain-model">
                      {t('geodataPage.details.terrainModel', { ns: 'cloudProcessing' })}
                    </label>
                    <span aria-labelledby="terrain-model">
                      {props.selectedItem.terrainModel
                        ? t('yes', { ns: 'common' })
                        : t('no', { ns: 'common' })}
                    </span>
                  </InfoItem>

                  {isDefined(props.loadedItem) && (
                    <InfoItem>
                      <label id="filtered-pointcloud">
                        {t('geodataPage.details.filteredPointCloud', { ns: 'cloudProcessing' })}
                      </label>
                      <span aria-labelledby="filtered-pointcloud">
                        {filteredPointCloudInfo ?? t('no', { ns: 'common' })}
                      </span>
                    </InfoItem>
                  )}
                </>
              )}
            </InfoRow>
          </>
        )}
      </Component>
    </OverlayLoader>
  );
};

const Component = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1em;
`;

const InfoItem = styled.div`
  width: 30%;

  label {
    font-weight: 400;
    font-size: 0.9em;
    display: block;
    margin-bottom: 0.2em;
  }

  span {
    font-weight: 300;
    display: block;
    font-weight: 300;
    font-size: 0.9em;
  }
`;

const InfoRow = styled.div`
  display: flex;
  flex-direction: row;
`;

export { GeodataDetails };
